// Custom email + password auth for the S7 Email Builder (P-045 / D-140 +
// P-046 / D-141 CRUD helpers).
//
// Implements V2.1 auth per `spec/prd-v2-auth.md` + `spec/tech-arch-v2-auth.md`
// §2 (P-045 login/session/signOut) + §4 (P-046 Settings CRUD). Credentials
// live in a Firestore `users` collection keyed by the lowercased email.
// Passwords stored as bcrypt hashes (cost 10) — never plaintext. The
// "logged-in" signal is a localStorage flag (`s7-email-builder.session.v1`)
// carrying `{ email }`.
//
// **Acknowledged trade-off.** This is "rolling our own auth" — accepted
// risk at chapter scale (5–10 staff, newsletter content). The architectural
// risk + mitigations are documented in `prd-v2-auth.md` §Security posture.
//
// **Static imports forbidden** — consumers must
// `await import('../lib/auth')` so Vite code-splits the Firestore SDK +
// bcryptjs into a lazy chunk (same constraint as `firebase-storage.ts`).
// `signOut()` is sync but lives here for cohesion; the chunk loads once
// per session at AuthGate boot, so a later sign-out click reuses it.
//
// **Anonymous Auth gate.** Firestore rules require `request.auth != null`.
// The existing `ensureAnonAuth()` helper from `firebase-storage.ts`
// satisfies that. AuthGate calls it once at mount per D-140 sub-decision
// 3 (engineering call from handoff Q4).
//
// **Operational-error pattern (P-045 close refinement).** Every Firestore
// round-trip is wrapped in try/catch; on failure we `console.error` the
// raw error for DevTools inspection and re-throw a sanitized user-facing
// message. Login distinguishes wrong-credentials (`INVALID_CREDENTIALS`)
// from operational failure (`SIGNIN_FAILED`); CRUD ops collapse all
// operational failures to a single `OPERATION_FAILED` string.

import bcrypt from 'bcryptjs'
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
} from 'firebase/firestore'

import { getFirebaseFirestore } from './firebase'

export const SESSION_STORAGE_KEY = 's7-email-builder.session.v1'

/** Bcrypt work factor. ~100 ms compute on a typical 2026 laptop;
 *  fast enough for instant-feeling login UX, slow enough that offline
 *  brute-force on a leaked hash is impractical for any non-trivial
 *  password. Banks-confirmed at session opening (P-045 Q2). */
export const BCRYPT_COST = 10

/** Minimum password length enforced at Add User / Reset Password.
 *  Banks-confirmed at P-046 session opening (Q1) — defensive floor
 *  above the PRD's "no validation" stance. Industry standard 8 chars;
 *  does not enforce composition rules (uppercase / digits / specials)
 *  per PRD §Out of scope. Enforced at both the UI layer (for inline
 *  validation feedback) and here (defense in depth — future non-UI
 *  callers also get the floor). */
export const MIN_PASSWORD_LENGTH = 8

export interface Session {
  readonly email: string
}

/** Public projection of `UserDoc` that omits the bcrypt hash. Returned
 *  by `listUsers()`; we never let the credential material reach the UI
 *  even though Firestore rules grant the calling staffer read access on
 *  the full doc (defense in depth at the API surface). */
export interface UserSummary {
  readonly email: string
  readonly createdAt: number
}

interface UserDoc {
  readonly email: string
  readonly passwordHash: string
  readonly createdAt: number
}

/** Generic error surfaced to staff for wrong credentials — same string
 *  for unknown email AND wrong password to prevent user enumeration
 *  (PRD §Functional requirements). */
const INVALID_CREDENTIALS = 'Invalid email or password.'

/** Surfaced when the Firestore round-trip itself fails (network down,
 *  rules misconfiguration, project unreachable). Distinguished from
 *  INVALID_CREDENTIALS so staff isn't told they typed the wrong password
 *  when actually the service is unreachable. Original error gets logged
 *  to `console.error` for Banks to inspect via DevTools. */
const SIGNIN_FAILED = "Couldn't sign in. Please try again."

/** Surfaced when Add User detects an existing doc at the canonicalized
 *  email. Pre-check round-trip + this string > overwrite-then-discover
 *  (the latter would leak rule-structure errors and lose the prior
 *  credential to no benefit). */
const USER_ALREADY_EXISTS = 'A user with this email already exists.'

/** Surfaced when the proposed password is below `MIN_PASSWORD_LENGTH`.
 *  Templates the configured floor so the UI shows the same number it
 *  hints at field-side. */
const PASSWORD_TOO_SHORT = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`

/** Catch-all for non-credential Firestore failures from the CRUD
 *  helpers. The Settings UI shows this inline inside the failing
 *  modal; the user retries. Original error → `console.error` for
 *  DevTools inspection. */
const OPERATION_FAILED = "Couldn't complete this request. Please try again."

/** Canonicalize + validate an email arg for the CRUD helpers. Returns
 *  the lowercased + trimmed form (matches the Firestore doc-ID
 *  convention used at every read/write seam). Throws "Email is
 *  required." for an empty-after-trim arg. Hoisted at the 3rd use
 *  (createUser + updateUserPassword + deleteUser).
 *
 *  `login()` keeps its own check inline — it folds the email + password
 *  empty validation into a single `INVALID_CREDENTIALS` throw, which
 *  is a different error contract than the CRUD helpers want. */
function requireEmail(email: string): string {
  const normalized = email.trim().toLowerCase()
  if (normalized === '') throw new Error('Email is required.')
  return normalized
}

/** Sign in with email + password. Throws INVALID_CREDENTIALS for any
 *  wrong-credentials case (unknown email OR bad password — same string
 *  so the UI can't be used to enumerate which emails exist). Throws
 *  SIGNIN_FAILED for any unexpected Firestore/network error. */
export async function login(email: string, password: string): Promise<void> {
  const normalized = email.trim().toLowerCase()
  if (normalized === '' || password === '') throw new Error(INVALID_CREDENTIALS)

  const firestore = getFirebaseFirestore()
  let snap
  try {
    snap = await getDoc(doc(firestore, 'users', normalized))
  } catch (err) {
    console.error('auth.login: Firestore getDoc failed', err)
    throw new Error(SIGNIN_FAILED)
  }
  if (!snap.exists()) throw new Error(INVALID_CREDENTIALS)

  const data = snap.data() as UserDoc
  const valid = await bcrypt.compare(password, data.passwordHash)
  if (!valid) throw new Error(INVALID_CREDENTIALS)

  setStoredSession({ email: normalized })
}

/** Read the current session, validating against Firestore that the user
 *  still exists. Returns `null` when the session is missing, malformed,
 *  or the user was deleted — and clears the stale localStorage flag in
 *  the deleted-user case. On transient Firestore errors (network,
 *  permission flap) returns `null` WITHOUT clearing storage, so the
 *  next attempt can recover the session once Firestore is reachable.
 *  Per Tech Arch §2.2 + the operational-error refinement at D-140. */
export async function getCurrentSession(): Promise<Session | null> {
  const stored = getStoredSession()
  if (!stored) return null
  const firestore = getFirebaseFirestore()
  let snap
  try {
    snap = await getDoc(doc(firestore, 'users', stored.email))
  } catch (err) {
    console.error('auth.getCurrentSession: Firestore getDoc failed', err)
    return null
  }
  if (!snap.exists()) {
    clearStoredSession()
    return null
  }
  return stored
}

/** Clear the session flag + reload. Reloading is the simplest way to
 *  re-trigger the AuthGate's mount effect and route back to LoginScreen.
 *  Per Tech Arch §2.3. */
export function signOut(): void {
  clearStoredSession()
  location.reload()
}

/** List every user in the Firestore `users` collection. Returns the
 *  `{email, createdAt}` projection — `passwordHash` is intentionally
 *  stripped at this seam so it never reaches consumers (defense in
 *  depth; Firestore rules grant read access on the full doc per V2.1
 *  flat-permission model, but the UI has no business handling the
 *  hash). Per Tech Arch v2-auth §4 deliverable 1. */
export async function listUsers(): Promise<readonly UserSummary[]> {
  const firestore = getFirebaseFirestore()
  let snap
  try {
    snap = await getDocs(collection(firestore, 'users'))
  } catch (err) {
    console.error('auth.listUsers: Firestore getDocs failed', err)
    throw new Error(OPERATION_FAILED)
  }
  return snap.docs.map((d) => {
    const data = d.data() as UserDoc
    return { email: data.email, createdAt: data.createdAt }
  })
}

/** Create a new user with the given email + password. Pre-checks
 *  existence at the canonicalized email so duplicates surface a
 *  friendly `USER_ALREADY_EXISTS` error before any write; without the
 *  pre-check, `setDoc` would silently overwrite the existing entry and
 *  destroy the prior credential.
 *
 *  Enforces `MIN_PASSWORD_LENGTH` even though the UI also validates —
 *  belt-and-suspenders against a future caller that skips the modal.
 *
 *  Throws (re-thrown via `new Error`, so the consumer can show
 *  `err.message` directly):
 *    - `PASSWORD_TOO_SHORT` when `password.length < MIN_PASSWORD_LENGTH`
 *    - `USER_ALREADY_EXISTS` when a doc already exists at the
 *      canonicalized email
 *    - `OPERATION_FAILED` on any Firestore round-trip failure
 */
export async function createUser(email: string, password: string): Promise<void> {
  const normalized = requireEmail(email)
  if (password.length < MIN_PASSWORD_LENGTH) throw new Error(PASSWORD_TOO_SHORT)

  const firestore = getFirebaseFirestore()
  const ref = doc(firestore, 'users', normalized)
  let existing
  try {
    existing = await getDoc(ref)
  } catch (err) {
    console.error('auth.createUser: Firestore getDoc failed', err)
    throw new Error(OPERATION_FAILED)
  }
  if (existing.exists()) throw new Error(USER_ALREADY_EXISTS)

  const passwordHash = await bcrypt.hash(password, BCRYPT_COST)
  try {
    await setDoc(ref, {
      email: normalized,
      passwordHash,
      createdAt: Date.now(),
    } satisfies UserDoc)
  } catch (err) {
    console.error('auth.createUser: Firestore setDoc failed', err)
    throw new Error(OPERATION_FAILED)
  }
}

/** Reset an existing user's password. Uses `updateDoc` so non-credential
 *  fields (createdAt, email) are preserved without a read-modify-write.
 *  If the target doc was deleted concurrently in another tab,
 *  `updateDoc` fails with a not-found error and we surface
 *  `OPERATION_FAILED` — the user's next list refresh shows the
 *  deletion. Enforces `MIN_PASSWORD_LENGTH` same as `createUser`. */
export async function updateUserPassword(email: string, newPassword: string): Promise<void> {
  const normalized = requireEmail(email)
  if (newPassword.length < MIN_PASSWORD_LENGTH) throw new Error(PASSWORD_TOO_SHORT)

  const firestore = getFirebaseFirestore()
  const passwordHash = await bcrypt.hash(newPassword, BCRYPT_COST)
  try {
    await updateDoc(doc(firestore, 'users', normalized), { passwordHash })
  } catch (err) {
    console.error('auth.updateUserPassword: Firestore updateDoc failed', err)
    throw new Error(OPERATION_FAILED)
  }
}

/** Delete a user. The "cannot remove self" guard lives at the caller
 *  (SettingsScreen) — the data layer is unconstrained so future tools
 *  (admin CLI, etc.) can remove any account if needed. Firestore
 *  `deleteDoc` on a missing doc succeeds silently, so concurrent
 *  deletion in another tab does not raise. */
export async function deleteUser(email: string): Promise<void> {
  const normalized = requireEmail(email)

  const firestore = getFirebaseFirestore()
  try {
    await deleteDoc(doc(firestore, 'users', normalized))
  } catch (err) {
    console.error('auth.deleteUser: Firestore deleteDoc failed', err)
    throw new Error(OPERATION_FAILED)
  }
}

function getStoredSession(): Session | null {
  const raw = localStorage.getItem(SESSION_STORAGE_KEY)
  if (raw === null) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'email' in parsed &&
      typeof (parsed as { email: unknown }).email === 'string'
    ) {
      // Re-canonicalize even though `login()` always stores lowercased.
      // Defensive against manual DevTools / browser-extension writes that
      // could mismatch the Firestore doc-ID convention and strand the
      // session in a "user not found" loop.
      const email = (parsed as { email: string }).email.trim().toLowerCase()
      if (email !== '') return { email }
    }
    clearStoredSession()
    return null
  } catch {
    clearStoredSession()
    return null
  }
}

function setStoredSession(session: Session): void {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
}

function clearStoredSession(): void {
  localStorage.removeItem(SESSION_STORAGE_KEY)
}
